import RichHtml from "@/components/ui/RichHtml";
import { resolveImageUrl } from "@/utils/imageUrl";
import { buildGradientCss } from "@/utils/gradientOverlay";
import { useSiteStyles } from "@/hooks/useSiteStyles";

// Portrait, social-ad-style graphic: a background image (zoom/transparency/
// blur/"reduce" height all adjustable), a company logo pill fixed top-left
// over it, and up to 4 repeatable rich-text cards an admin can place above,
// below or overlapping the image — e.g. a headline pill above and a price
// comparison + CTA card overlapping the bottom.
// Reused across CardsGridModule's own copy of these exact shadow presets —
// kept in sync by name/values, not shared, to avoid cross-module coupling.
const ADVERT_CARD_SHADOW_PRESETS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};

function cardStyle(card) {
  const borderWidth = Number(card.border_width || 0);
  const style = {
    backgroundColor: card.bg_color || "#ffffff",
    borderRadius: `${card.corner_radius ?? 20}px`,
    border: borderWidth > 0 ? `${borderWidth}px solid ${card.border_color || "#e5e7eb"}` : undefined,
    boxShadow: ADVERT_CARD_SHADOW_PRESETS[card.card_shadow || "none"] || undefined,
    padding: `${card.padding ?? 24}px`,
    maxWidth: card.max_width ? `${card.max_width}px` : "100%",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
    pointerEvents: "auto",
  };
  const overlap = card.overlap_px ?? 48;
  if (card.position === "below-overlap") style.marginTop = `-${overlap}px`;
  else if (card.position === "below") style.marginTop = "12px";
  if (card.position === "above-overlap") style.marginBottom = `-${overlap}px`;
  else if (card.position === "above") style.marginBottom = "12px";
  return style;
}

function AdvertCard({ card }) {
  if (!card.content_html) return null;
  return (
    <div style={cardStyle(card)} data-testid="advert-card">
      <RichHtml className="prose prose-sm max-w-none" html={card.content_html} />
    </div>
  );
}

export default function AdvertModule({ config }) {
  const { styles: siteStyles } = useSiteStyles();
  const cards = Array.isArray(config.cards) ? config.cards : [];
  const aboveCards = cards.filter((c) => c.position === "above" || c.position === "above-overlap");
  const belowCards = cards.filter((c) => !c.position || c.position === "below" || c.position === "below-overlap");
  const centerOverlayCards = cards.filter((c) => c.position === "center-overlay");
  const bottomOverlayCards = cards.filter((c) => c.position === "bottom-overlay");
  // The biggest "dips into the image" overlap any above-card uses — pushes
  // the logo badge down below that so an overlapping card never covers it.
  const maxAboveOverlap = Math.max(0, ...aboveCards
    .filter((c) => c.position === "above-overlap")
    .map((c) => Number(c.overlap_px ?? 48)));

  // Always the site's Company Logo (Admin → Site Styles) — same logo used
  // in the navigation — so the ad always stays on-brand automatically.
  const logoUrl = siteStyles?.companyLogo || "";
  const showLogo = config.logo_enabled !== false && logoUrl;

  return (
    <div
      style={{
        backgroundColor: config.container_bg_color || "#102a3d",
        padding: `${config.container_padding ?? 20}px`,
        borderRadius: `${config.container_radius ?? 16}px`,
        maxWidth: `${config.container_max_width || 420}px`,
        margin: "0 auto",
      }}
      data-testid="advert-module"
    >
      <div>
        {aboveCards.map((card, idx) => <AdvertCard key={card.id || `above-${idx}`} card={card} />)}

        {config.background_image && (
          // CSS Grid "stack": every layer shares grid cell 1/1, so the row
          // auto-sizes to whichever is tallest — the fixed-height image, or
          // an overlay card whose image+copy needs more room. Nothing gets
          // clipped the way a fixed-height `overflow:hidden` wrapper would.
          <div style={{ display: "grid", position: "relative", borderRadius: "12px" }} data-testid="advert-image-frame">
            <div style={{ gridRow: 1, gridColumn: 1, height: `${config.image_height || 320}px`, overflow: "hidden", borderRadius: "12px", position: "relative" }}>
              <img
                src={resolveImageUrl(config.background_image)}
                alt=""
                className="w-full h-full object-cover"
                style={{
                  transform: `scale(${(config.image_zoom || 100) / 100})`,
                  opacity: (config.image_opacity ?? 100) / 100,
                  filter: [
                    config.image_blur ? `blur(${config.image_blur}px)` : null,
                    (config.image_brightness ?? 100) !== 100 ? `brightness(${(config.image_brightness ?? 100) / 100})` : null,
                  ].filter(Boolean).join(" ") || undefined,
                }}
              />
              {buildGradientCss(config.image_gradient, config.image_gradient_color, config.image_gradient_opacity) && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: buildGradientCss(config.image_gradient, config.image_gradient_color, config.image_gradient_opacity) }}
                  data-testid="advert-image-gradient-overlay"
                />
              )}
            </div>

            {showLogo && (
              <div style={{ gridRow: 1, gridColumn: 1, pointerEvents: "none" }}>
                <div
                  className="absolute left-4 flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-md z-10"
                  style={{ top: `${16 + maxAboveOverlap}px`, pointerEvents: "auto" }}
                  data-testid="advert-logo-badge"
                >
                  <img src={resolveImageUrl(logoUrl)} alt="" className="h-8 w-auto max-w-[120px] object-contain" />
                  {config.logo_text && <span className="text-sm font-bold text-foreground">{config.logo_text}</span>}
                </div>
              </div>
            )}

            {centerOverlayCards.length > 0 && (
              <div style={{ gridRow: 1, gridColumn: 1, pointerEvents: "none" }} className="flex flex-col items-center justify-center gap-2 p-4">
                {centerOverlayCards.map((card, idx) => <AdvertCard key={card.id || `overlay-${idx}`} card={card} />)}
              </div>
            )}

            {bottomOverlayCards.length > 0 && (
              <div style={{ gridRow: 1, gridColumn: 1, pointerEvents: "none" }} className="flex flex-col items-center justify-end gap-2 p-4" data-testid="advert-bottom-overlay">
                {bottomOverlayCards.map((card, idx) => <AdvertCard key={card.id || `bottom-overlay-${idx}`} card={card} />)}
              </div>
            )}
          </div>
        )}

        {belowCards.map((card, idx) => <AdvertCard key={card.id || `below-${idx}`} card={card} />)}
      </div>
    </div>
  );
}
